/**
 * @fileoverview React Context for sharing order-related state and configuration across components.
 * Provides a centralized way to access order display settings and vendor-specific configurations.
 *
 * @author Web Analysis Team
 * @version 1.0.0
 */

import { createContext } from 'react';

/**
 * React Context for order management components.
 *
 * This context provides shared state and configuration values for order-related components,
 * including display preferences, vendor settings, and system note visibility.
 *
 * @type {React.Context<Object>}
 *
 * @typedef {Object} OrderContextValue
 * @property {boolean} showSystemNotes - Whether to display system-generated notes
 * @property {Function} setShowSystemNotes - State setter for system notes visibility
 * @property {boolean} isResale - Whether the current context is for resale orders
 * @property {boolean} usesStrOrderNumber - Whether to use string-based order numbers instead of numeric
 *
 * @example
 * // Using the OrderContext in a component
 * import { useContext } from 'react';
 * import { OrderContext } from './hooks/OrderContext';
 *
 * const MyOrderComponent = () => {
 *   const { showSystemNotes, setShowSystemNotes, isResale } = useContext(OrderContext);
 *
 *   return (
 *     <div>
 *       {isResale && <div>Resale-specific content</div>}
 *       <button onClick={() => setShowSystemNotes(!showSystemNotes)}>
 *         {showSystemNotes ? 'Hide' : 'Show'} System Notes
 *       </button>
 *     </div>
 *   );
 * };
 */
export const OrderContext = createContext();